import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiError, fmtINR } from "@/lib/api";
import { PageHeader, StatusBadge, DEPARTMENTS } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, FileText, Trash2, Loader2, Mail, Phone, CalendarClock, Download, Ban, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { InterviewScheduler } from "@/components/InterviewScheduler";

const STAGES = ["Applied", "Interviewing", "Offered", "Hired", "Rejected"];
const empty = { name: "", email: "", phone: "", role: "", department: "Engineering", expected_ctc: 1200000, notice_period: "30 days", joining_date: "", stage: "Applied", score: 70 };

export default function Recruitment() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [schedFor, setSchedFor] = useState(null);
  const [editIv, setEditIv] = useState(null);

  const { data: interviews = [] } = useQuery({
    queryKey: ["interviews"], queryFn: async () => (await api.get("/interviews")).data,
  });
  const ivStatusMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/interviews/${id}`, { status })).data,
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ["interviews"] }); toast.success(v.status === "Cancelled" ? "Interview cancelled — candidate notified" : "Interview marked completed"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const downloadIcs = async (iv) => {
    const res = await api.get(`/interviews/${iv.id}/ics`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `interview-${iv.candidate_name.replace(/\s+/g, "_")}.ics`; a.click();
    URL.revokeObjectURL(url);
  };
  const upcoming = interviews.filter((i) => i.status !== "Cancelled" && i.status !== "Completed");

  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["candidates"],
    queryFn: async () => (await api.get("/candidates")).data,
  });

  const addMut = useMutation({
    mutationFn: async (body) => (await api.post("/candidates", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["candidates"] }); setOpen(false); setForm(empty); toast.success("Candidate added"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const stageMut = useMutation({
    mutationFn: async ({ id, stage }) => (await api.patch(`/candidates/${id}/stage`, { stage })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["candidates"] }); toast.success("Stage updated"); },
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/candidates/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["candidates"] }); toast.success("Candidate removed"); },
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <PageHeader title="Recruitment & Candidates" subtitle="Manage your hiring pipeline and generate offers.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="candidate-add-button" className="rounded-lg font-semibold active:scale-[0.98]">
              <Plus className="w-4 h-4 mr-1.5" /> Add Candidate
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">New Candidate</DialogTitle><DialogDescription>Add a candidate to the recruitment pipeline.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="space-y-1.5 col-span-2"><Label>Full Name</Label><Input data-testid="candidate-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="candidate-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="candidate-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Role / Designation</Label><Input data-testid="candidate-role-input" value={form.role} onChange={(e) => set("role", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Department</Label>
                <Select value={form.department} onValueChange={(v) => set("department", v)}>
                  <SelectTrigger data-testid="candidate-department-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Expected CTC (₹/yr)</Label><Input data-testid="candidate-ctc-input" type="number" value={form.expected_ctc} onChange={(e) => set("expected_ctc", Number(e.target.value))} /></div>
              <div className="space-y-1.5"><Label>Notice Period</Label><Input data-testid="candidate-notice-input" value={form.notice_period} onChange={(e) => set("notice_period", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Expected Joining</Label><Input data-testid="candidate-joining-input" type="date" value={form.joining_date} onChange={(e) => set("joining_date", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Evaluation Score</Label><Input data-testid="candidate-score-input" type="number" value={form.score} onChange={(e) => set("score", Number(e.target.value))} /></div>
            </div>
            <DialogFooter>
              <Button onClick={() => addMut.mutate(form)} disabled={addMut.isPending || !form.name || !form.email || !form.role} data-testid="candidate-save-button" className="rounded-lg">
                {addMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Candidate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {upcoming.length > 0 && (
        <Card className="border border-border rounded-xl overflow-hidden mb-6" data-testid="upcoming-interviews">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2"><CalendarClock className="w-4 h-4 text-accent" /><h3 className="font-heading font-semibold">Upcoming Interviews</h3><span className="text-xs font-mono text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">{upcoming.length}</span></div>
          <div className="divide-y divide-border">
            {upcoming.map((iv) => (
              <div key={iv.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`interview-row-${iv.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{iv.candidate_name} <span className="text-muted-foreground font-normal">• {iv.role}</span></p>
                  <p className="text-xs text-muted-foreground font-mono">{new Date(iv.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} • {iv.duration_min} min • {iv.mode}{iv.interviewer ? ` • ${iv.interviewer}` : ""}</p>
                </div>
                <StatusBadge status={iv.status === "Rescheduled" ? "Interviewing" : "Offered"} />
                <span className="text-[10px] font-mono text-muted-foreground">{iv.status}</span>
                <Button size="sm" variant="ghost" onClick={() => downloadIcs(iv)} title="Download .ics" data-testid={`interview-ics-${iv.id}`}><Download className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => setEditIv(iv)} data-testid={`interview-reschedule-${iv.id}`}><CalendarClock className="w-3.5 h-3.5 mr-1" /> Reschedule</Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => ivStatusMut.mutate({ id: iv.id, status: "Completed" })} title="Mark completed" data-testid={`interview-complete-${iv.id}`}><CheckCheck className="w-4 h-4 text-emerald-600" /></Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => ivStatusMut.mutate({ id: iv.id, status: "Cancelled" })} title="Cancel" data-testid={`interview-cancel-${iv.id}`}><Ban className="w-4 h-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <InterviewScheduler candidate={schedFor} interview={editIv} onClose={() => { setSchedFor(null); setEditIv(null); }} />

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {STAGES.map((stage) => {
            const list = candidates.filter((c) => c.stage === stage);
            return (
              <div key={stage} className="space-y-3" data-testid={`kanban-column-${stage}`}>
                <div className="flex items-center justify-between px-1">
                  <span className="text-sm font-semibold font-heading">{stage}</span>
                  <span className="text-xs font-mono text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">{list.length}</span>
                </div>
                <div className="space-y-3">
                  {list.map((c) => (
                    <Card key={c.id} className="p-4 border border-border rounded-xl hover:shadow-md transition-shadow group" data-testid={`candidate-card-${c.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{c.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{c.role}</p>
                        </div>
                        <button onClick={() => delMut.mutate(c.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition" data-testid={`delete-candidate-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                        <p className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3" /> {c.email}</p>
                        <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {c.phone || "—"}</p>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs font-mono font-semibold text-accent">{fmtINR(c.expected_ctc)}</span>
                        <span className="text-[10px] font-mono text-muted-foreground">Score {c.score}</span>
                      </div>
                      <div className="mt-3 flex gap-1.5">
                        <Select value={c.stage} onValueChange={(v) => stageMut.mutate({ id: c.id, stage: v })}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`stage-select-${c.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Button
                        size="sm" variant="outline"
                        className="w-full mt-2 h-8 text-xs rounded-lg"
                        data-testid={`schedule-interview-button-${c.id}`}
                        onClick={() => setSchedFor(c)}
                      >
                        <CalendarClock className="w-3.5 h-3.5 mr-1" /> Schedule Interview
                      </Button>
                      <Button
                        size="sm" variant="secondary"
                        className="w-full mt-2 h-8 text-xs rounded-lg"
                        data-testid={`generate-offer-button-${c.id}`}
                        onClick={() => navigate("/offers", { state: { candidate: c } })}
                      >
                        <FileText className="w-3.5 h-3.5 mr-1" /> Generate Offer
                      </Button>
                    </Card>
                  ))}
                  {list.length === 0 && <p className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border rounded-xl">No candidates</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
